import { useEffect } from "react";
import { CustomizationData } from "@/contexts/CustomizationContext";

/**
 * PreviewListener Component
 * 
 * Listens for customization updates from the admin preview panel
 * and applies them in real-time to the marketplace site.
 * 
 * This component should be included in the marketplace layout
 * to enable live preview functionality.
 */
export function PreviewListener() {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Security: In production, validate event.origin
      // For now, we accept messages from any origin for preview functionality
      
      if (event.data.type === 'CUSTOMIZATION_UPDATE') {
        const data = event.data.data as CustomizationData;
        console.log('[PreviewListener] Received customization update:', data);
        
        // Apply CSS variables for colors
        if (data.primaryColor) {
          document.documentElement.style.setProperty('--preview-primary', data.primaryColor);
        }
        
        // Update text content
        updateTextContent('[data-preview="companyName"]', data.companyName);
        updateTextContent('[data-preview="heroTitle"]', data.heroTitle);
        updateTextContent('[data-preview="heroSubtitle"]', data.heroSubtitle);
        updateTextContent('[data-preview="heroDescription"]', data.heroDescription);
        updateTextContent('[data-preview="heroPrimaryButtonText"]', data.heroPrimaryButtonText);
        updateTextContent('[data-preview="heroSecondaryButtonText"]', data.heroSecondaryButtonText);
        updateTextContent('[data-preview="heroTrustIndicator1"]', data.heroTrustIndicator1);
        updateTextContent('[data-preview="heroTrustIndicator2"]', data.heroTrustIndicator2);
        updateTextContent('[data-preview="aboutText"]', data.aboutText);
        updateTextContent('[data-preview="displayName"]', data.displayName);
        updateTextContent('[data-preview="phone"]', data.phone);
        updateTextContent('[data-preview="email"]', data.email);
        
        // Update images
        updateImageSrc('[data-preview="logoUrl"]', data.logoUrl);
        updateImageSrc('[data-preview="heroImageUrl"]', data.heroImageUrl);
        updateImageSrc('[data-preview="aboutImageUrl"]', data.aboutImageUrl);
        updateImageSrc('[data-preview="avatarUrl"]', data.avatarUrl);
        
        // Update favicon
        if (data.faviconUrl) {
          updateFavicon(data.faviconUrl);
        }
        
        // Dispatch custom event for template switching
        if (data.siteTemplate) {
          window.dispatchEvent(new CustomEvent('preview:template-change', {
            detail: { template: data.siteTemplate }
          }));
        }
      } else if (event.data.type === 'HIGHLIGHT_FIELD') {
        const field = event.data.field;
        console.log('[PreviewListener] Highlighting field:', field);
        highlightField(field);
      } else if (event.data.type === 'CLEAR_HIGHLIGHT') {
        console.log('[PreviewListener] Clearing highlights');
        clearHighlights();
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return null; // This component doesn't render anything
}

// Helper functions

function updateTextContent(selector: string, value: string) {
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => {
    if (element instanceof HTMLElement) {
      element.textContent = value;
    }
  });
}

function updateImageSrc(selector: string, value: string) {
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => {
    if (element instanceof HTMLImageElement && value) {
      element.src = value;
    }
  });
}

function updateFavicon(url: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}

function highlightField(field: string) {
  // Clear existing highlights
  clearHighlights();
  
  // Map field names to preview selectors
  const fieldMap: Record<string, string> = {
    companyName: '[data-preview="companyName"]',
    logoUrl: '[data-preview="logoUrl"]',
    heroTitle: '[data-preview="heroTitle"]',
    heroSubtitle: '[data-preview="heroSubtitle"]',
    heroDescription: '[data-preview="heroDescription"]',
    heroImageUrl: '[data-preview="heroImageUrl"]',
    heroPrimaryButtonText: '[data-preview="heroPrimaryButtonText"]',
    heroSecondaryButtonText: '[data-preview="heroSecondaryButtonText"]',
    heroTrustIndicator1: '[data-preview="heroTrustIndicator1"]',
    heroTrustIndicator2: '[data-preview="heroTrustIndicator2"]',
    aboutText: '[data-preview="aboutText"]',
    aboutImageUrl: '[data-preview="aboutImageUrl"]',
    displayName: '[data-preview="displayName"]',
    avatarUrl: '[data-preview="avatarUrl"]',
    phone: '[data-preview="phone"]',
    email: '[data-preview="email"]',
  };
  
  const selector = fieldMap[field];
  if (!selector) {
    return;
  }
  
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => {
    if (element instanceof HTMLElement) {
      element.classList.add('preview-highlight');
      element.style.outline = '2px solid #0EA5E9';
      element.style.outlineOffset = '2px';
      element.style.transition = 'outline 0.2s ease-in-out';
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

function clearHighlights() {
  const elements = document.querySelectorAll('.preview-highlight');
  elements.forEach(element => {
    if (element instanceof HTMLElement) {
      element.classList.remove('preview-highlight');
      element.style.outline = '';
      element.style.outlineOffset = '';
    }
  });
}
